import { RouterStore } from '@estee/elc-router';

import {
    AccountSignInSignUpSearchParams,
    accountSignUpFormConfigName,
    IamType
} from '~constants/Constants';
import { SocialPageLoadType } from '~constants/SocialConstants';
import { BaseViewController } from '~exported/BaseViewController';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { UserRepository } from '~repositories/UserRepository';
import {
    ACCOUNT_GUEST_CHECKOUT_ROUTE,
    ACCOUNT_SIGN_IN_ROUTE,
    ACCOUNT_SIGN_UP_ROUTE
} from '~routes/routes';
import { getSanitizedRedirectUrl, localeRouteBuilder } from '~routes/utils';
import { ConfigurationLoadedUseCase } from '~use-cases/ConfigurationLoadedUseCase';
import { SocialPageLoadedUseCase } from '~use-cases/social/SocialPageLoadedUseCase';
import { SocialSignUpCallbackLoadedUseCase } from '~use-cases/social/SocialSignUpCallbackLoadedUseCase';
import { TaggingEventSourceType } from '~utils/Analytics';
import { getSocialPageLoadType } from '~utils/SocialUtils';

interface IRampSignInSignUpViewController {
    routerStore: RouterStore;
    configRepository: ConfigRepository;
    userRepository: UserRepository;
}

export class RampSignInSignUpViewController extends BaseViewController {
    private configRepository: ConfigRepository;

    constructor({
        routerStore,
        configRepository,
        userRepository
    }: IRampSignInSignUpViewController) {
        super({ routerStore, userRepository });
        this.routerStore = routerStore;
        this.configRepository = configRepository;
        this.userRepository = userRepository;
    }

    public loaded = async () => {
        try {
            super.init();
            const { iamType } = this.configRepository;
            const { loadSession } = this.userRepository;
            const socialPageLoadType = getSocialPageLoadType();

            if (
                socialPageLoadType === SocialPageLoadType.STATE ||
                iamType === IamType.SOCIAL_SIGN_UP
            ) {
                this.setIsLoading(true);
                // should call get session api before post session/authCode for social sign-in/ sign-up
                // https://confluence.esteeonline.com/display/AFE/Social+Login+%3E+OAuth+Flow
                await loadSession();
            }

            // We have social sign-in in social sign-up flow, it need to be handled first
            if (socialPageLoadType) {
                this.setEventSource();
                await new SocialPageLoadedUseCase(socialPageLoadType).execute();
                this.setIsLoading(false);

                return;
            }

            if (iamType === IamType.SOCIAL_SIGN_UP) {
                // Social sign up flow: KKG / SSG
                await new SocialSignUpCallbackLoadedUseCase().execute();
                this.setIsLoading(false);

                return;
            }
        } catch (error) {
            this.setError(error);
        }
        this.watchSessionStateChange();
        this.setEventSource();
        this.signInSignupLoaded();
    };

    public getRedirectUrl = (urlParams: URLSearchParams): string => {
        let paramsRedirect = urlParams.get(AccountSignInSignUpSearchParams.REDIRECT_URL) || '';
        this.isCheckoutFlow = paramsRedirect.includes('/checkout');

        if (this.isCheckoutFlow) {
            const checkoutRedirect = localeRouteBuilder(paramsRedirect.replace('/', ''));
            paramsRedirect = `/${checkoutRedirect}`;
        }

        return paramsRedirect ? getSanitizedRedirectUrl(paramsRedirect) : paramsRedirect;
    };

    private setEventSource = () => {
        const { setSignUpEventSource, setSignInEventSource } = this.userRepository;
        setSignUpEventSource(
            this.isCheckoutFlow
                ? TaggingEventSourceType.CHECKOUT_SIGN_UP
                : TaggingEventSourceType.ACCOUNT
        );
        setSignInEventSource(
            this.isCheckoutFlow
                ? TaggingEventSourceType.CHECKOUT_SIGN_IN
                : TaggingEventSourceType.ACCOUNT
        );
    };

    private signInSignupLoaded = () => {
        const {
            setRedirectUrlOverride,
            setIsGuestAndSignIn,
            setIsCheckoutFlow,
            iamType
        } = this.configRepository;
        const { changeCurrentRoute } = this.routerStore;

        // TODO: this configuration is for sign up, for in-store RAMP or KKG/SSG, we do need it to be loaded first, but it is not the case for IAM if it's guest checkout flow as there is no sign up form
        new ConfigurationLoadedUseCase(accountSignUpFormConfigName).execute();

        // start | Vulcan routing until RouteStore works correctly
        const requestedRoute = window.location.pathname.replace('/', '');
        if (requestedRoute === ACCOUNT_SIGN_IN_ROUTE) {
            changeCurrentRoute(ACCOUNT_SIGN_IN_ROUTE);
        } else if (requestedRoute === ACCOUNT_GUEST_CHECKOUT_ROUTE) {
            changeCurrentRoute(ACCOUNT_GUEST_CHECKOUT_ROUTE);
        } else {
            changeCurrentRoute(ACCOUNT_SIGN_UP_ROUTE);
        }
        // finish | Vulcan routing

        setRedirectUrlOverride(this.redirectUrl || '');
        setIsGuestAndSignIn(iamType === IamType.GUEST_CHECKOUT || this.isGuest);
        setIsCheckoutFlow(this.isCheckoutFlow);
    };
}
